import React, { useCallback, useMemo } from 'react';

import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import CursorIcon from '@atlaskit/icon-lab/core/cursor';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ISSUE_NAVIGATOR_URL } from '../../../../../common/constants/issue-navigator';
import { PrimaryProductKeys } from '../../../../../common/constants/products';
import { SearchExperience } from '../../../../../common/constants/search-experience';
import { useAppContext } from '../../../../../common/ui/app-context';
import { Link } from '../../../../../common/ui/link-component';
import { serveJiraNINFpsNLQNavigation } from '../../../../../common/utils/experiments/experiment-exports';
import type { State } from '../../../../../common/utils/quick-find/state';
import { type QuickFindFooterEvent } from '../../../../../controllers/quick-find/utils';
import { useQuickFindQuery } from '../../../../../controllers/store/quick-find';

import { messages } from './messages';

const styles = xcss({ height: '100%', display: 'inline-block' });
const singleSiteContainer = xcss({
	padding: 'space.100',
});

const iconContainer = xcss({
	marginLeft: 'space.150',
	marginRight: 'space.200',
});

const styleLink = xcss({
	WebkitBoxAlign: 'baseline',
	alignItems: 'center',
	boxSizing: 'border-box',
	display: 'inline-flex',
	justifyContent: 'center',
	fontWeight: 'font.weight.medium',
	textAlign: 'center',
	whiteSpace: 'nowrap',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.small'),
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	marginRight: 'space.100',
	color: 'color.text.subtle',
	textDecoration: 'none',
	transition: 'background 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38)',
	':hover': {
		color: 'color.text.subtle',
		backgroundColor: 'color.background.neutral.hovered',
		textDecoration: 'none',
		transition: 'background 0s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38)',
	},
	':visited': {
		color: 'color.text.subtle',
	},
	':focus-visible': {
		color: 'color.text.subtle',
	},
	':focus': {
		color: 'color.text.subtle',
	},
});

type SearchConfiguration = {
	href: string;
	message: MessageDescriptor;
	actionSubjectId: string;
};

enum JiraFooterLinkActionSubjectId {
	JIRA_BOARDS = 'jiraBoardsSearchLink',
	JIRA_FILTERS = 'jiraFiltersSearchLink',
	JIRA_PROJECTS = 'jiraProjectsSearchLink',
	JIRA_PLANS = 'jiraPlansSearchLink',
	JIRA_PEOPLE = 'jiraPeopleSearchLink',
	JIRA_WORK_ITEMS = 'jiraWorkItemsSearchLink',
}

export const getSearchConfiguration: (query: string) => { [key: string]: SearchConfiguration } = (
	query,
) => {
	const allLinks: { [key: string]: SearchConfiguration } = {
		board: {
			href: `/secure/ManageRapidViews.jspa?contains=${query}`,
			message: messages.jira_quick_find_boards,
			actionSubjectId: JiraFooterLinkActionSubjectId.JIRA_BOARDS,
		},
		project: {
			href: `/projects?contains=${query}`,
			message: messages.jira_quick_find_spaces,
			actionSubjectId: JiraFooterLinkActionSubjectId.JIRA_PROJECTS,
		},
		filters: {
			href: `/secure/ManageFilters.jspa?Search=Search&filterView=search&name=${query}`,
			message: messages.jira_quick_find_filters,
			actionSubjectId: JiraFooterLinkActionSubjectId.JIRA_FILTERS,
		},
		plans: {
			href: `/jira/plans`,
			message: messages.jira_quick_find_plans,
			actionSubjectId: JiraFooterLinkActionSubjectId.JIRA_PLANS,
		},
		people: {
			href: `/jira/people/search?q=${query}`,
			message: messages.jira_quick_find_people,
			actionSubjectId: JiraFooterLinkActionSubjectId.JIRA_PEOPLE,
		},
	};

	return allLinks;
};

type Props = {
	state: State;
	onClick: (props: QuickFindFooterEvent) => void;
};

export const JiraSingleSiteSearch = ({ state, onClick }: Props): React.JSX.Element => {
	const quickFindQuery = useQuickFindQuery();
	const { generateSearchUrl, queryParams } = useAppContext();

	const searchConfiguration = getSearchConfiguration(quickFindQuery);

	const intl = useIntl();
	const intentRouterEnabled =
		serveJiraNINFpsNLQNavigation({
			primaryProduct: PrimaryProductKeys.Jira,
			fireLayerExposure: false, // Exposure is fired when a user calls the intent detection API
			locale: intl.locale,
		})?.cohort === 'test';

	const workItemsUrl = useMemo(() => {
		if (!intentRouterEnabled) {
			return;
		}

		try {
			return generateSearchUrl(
				{ ...queryParams, text: quickFindQuery, referrer: SearchExperience.QUICK_FIND },
				true,
				undefined,
				undefined,
				!fg('rovo_search_update_work_items_link'),
			);
		} catch {
			return ISSUE_NAVIGATOR_URL;
		}
	}, [generateSearchUrl, intentRouterEnabled, queryParams, quickFindQuery]);

	// Build final configuration including Work Items first if enabled
	const allLinks = useMemo(() => {
		if (workItemsUrl) {
			return {
				workItems: {
					href: workItemsUrl,
					message: messages.jira_quick_find_work_items,
					actionSubjectId: JiraFooterLinkActionSubjectId.JIRA_WORK_ITEMS,
				},
				...searchConfiguration,
			};
		}
		return searchConfiguration;
	}, [searchConfiguration, workItemsUrl]);

	const onFilterClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, actionSubjectId: string) => {
			onClick({ state, event, actionSubjectId });
		},
		[onClick, state],
	);

	return (
		<Box xcss={singleSiteContainer}>
			<Box as="span" xcss={iconContainer}>
				{/* For now we are using CompassIcon until a custom navigation icon is made for this */}
				<CursorIcon label="navigate-single-site-icon" />
			</Box>
			{Object.keys(allLinks).map((key) => {
				const { href, message, actionSubjectId } = allLinks[key];
				const LinkComponent = fg('qf_jira_links_spa_navigation') ? Link : Anchor;

				return (
					<Box key={`single_site_${key}`} role="none" xcss={styles}>
						<LinkComponent
							xcss={styleLink}
							href={href}
							onClick={(event) => onFilterClick(event, actionSubjectId)}
						>
							<FormattedMessage {...message} />
						</LinkComponent>
					</Box>
				);
			})}
		</Box>
	);
};
